import { NgRedux } from '@ptl/angular-redux-store';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { StateStructure } from '@ptl/state';
import { DisplayableFile, FILE_UPLOAD_STATUS, FILE_VALIDATION_FAILURE_STATUS } from '../interfaces/displayable-file.interface';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { BrandingConfigurationService } from '@ptl/brand';
import { AppInjector } from '@ptl/forms';
import { SuccessResponse } from './interfaces/sxesvc.api.v1.review.document.response.interface';
import { RequiredDocsUploadService } from '../services/required-docs-upload.service';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';

@Component({
    selector: '[app-file-display]',
    templateUrl: './file-display.component.html',
    styleUrls: ['./file-display.component.scss'],
    standalone: false
})
export class FileDisplayComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  @Input() onUploadFile: (fileComponent: FileDisplayComponent) => void;
  @Input() onRemoveFile: (fileComponent: FileDisplayComponent) => void;

  @Input() customPayloadAdditions: (payload: FormData, displayableFileObj: DisplayableFile) => void;

  @Input() displayableFileObj: DisplayableFile;
  @Input() actualFileObj: File;
  @Input() index: number;
  @Input() allowedExtensions: string[];
  @Input() fileSizeLimitInMB: number;
  @Input() canUploadMoreFiles: boolean;
  @Input() disableRemoveButtonOnSuccess: boolean;
  

  validForUpload: boolean;
  isPending: boolean;
  successfullySubmitted: boolean;

  disableRemoveButton: boolean;

  private injector: Injector;
  protected brandService: BrandingConfigurationService;
  protected httpClient: HTTPWrapperContext;
  protected ngRedux: NgRedux<StateStructure>;
  protected requiredDocsUploadService: RequiredDocsUploadService;
  protected reducerUtilityService: ReducerUtilityService;

  constructor() {
    this.injector = AppInjector.getInjector();
    this.brandService = this.injector.get(BrandingConfigurationService); 
    this.httpClient = this.injector.get(HTTPWrapperContext); 
    this.ngRedux = this.injector.get(NgRedux); 
    this.requiredDocsUploadService = this.injector.get(RequiredDocsUploadService);
    this.reducerUtilityService = this.injector.get(ReducerUtilityService)
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * Because we don't rely on the actual values
     * stored in the displayable/actual file objects
     * to display status, but instead run logic on their values:
     * we need to rerun the validation
     */

    this.setStatus(this.displayableFileObj.status);

    // This won't exist when pulling file from state ( only meta data saved to JSON structure / stringified / encrypted )
    if (this.actualFileObj) {
      this.validateSize(this.actualFileObj.size);
      this.validateFileType(this.actualFileObj.name);
    }
  }

  ngOnInit() {
    this.setStatus(this.displayableFileObj.status || FILE_UPLOAD_STATUS.NEW);

    // This won't exist when pulling file from state ( only meta data saved to JSON structure / stringified / encrypted )
    if (this.actualFileObj) {
      this.validateSize(this.actualFileObj.size);
      this.validateFileType(this.actualFileObj.name);
    }

  }

  ngOnDestroy() {
  }

  ngAfterViewInit() {
  }

  private validateSize(sizeInBytes: number): void {
    const sizeInMb = sizeInBytes / Math.pow(1024, 2);
    const sizeLimitInMb = this.fileSizeLimitInMB;

    /** Check for accepted file size */
    if (sizeInMb > sizeLimitInMb) {
      this.setStatus(FILE_VALIDATION_FAILURE_STATUS.FILE_SIZE_TOO_BIG);
    }
  }

  /**
   * Used for ternary operation to (1) determine if it exists and (2) determines validity
   * Extendable
   **/
  customValidForUploadLogic(): { exists: boolean; value: boolean } {
    return { exists: false, value: null };
  }

  private setStatus(status: FILE_UPLOAD_STATUS | FILE_VALIDATION_FAILURE_STATUS): void {

    const customUploadValidity = this.customValidForUploadLogic();

    switch (status) {
      case FILE_UPLOAD_STATUS.NEW:
        this.isPending = false;
        this.validForUpload = !customUploadValidity.exists ? true : customUploadValidity.value;
        this.successfullySubmitted = false;
        this.disableRemoveButton = false;
        break;
      case FILE_UPLOAD_STATUS.PENDING:
        this.isPending = true;
        this.validForUpload = false;
        this.successfullySubmitted = false;
        this.disableRemoveButton = true; // only disables manual removal of individual document; doesn't prevent removal when clicking remove all
        break;
      case FILE_UPLOAD_STATUS.SUCCESS:
        this.isPending = false;
        this.validForUpload = !customUploadValidity.exists ? true : customUploadValidity.value;
        this.successfullySubmitted = true;
        this.disableRemoveButton = this.disableRemoveButtonOnSuccess; // only disables manual removal of individual document; doesn't prevent removal when clicking remove all
        break;
      case FILE_UPLOAD_STATUS.FAILURE:
        this.isPending = false;
        this.validForUpload = !customUploadValidity.exists ? true : customUploadValidity.value;
        this.successfullySubmitted = false;
        this.disableRemoveButton = false;
        break;
      case FILE_VALIDATION_FAILURE_STATUS.FILE_SIZE_TOO_BIG:
        this.isPending = false;
        this.validForUpload = false;
        this.successfullySubmitted = false;
        this.disableRemoveButton = false;
        break;
      case FILE_VALIDATION_FAILURE_STATUS.FILE_TYPE:
        this.isPending = false;
        this.validForUpload = false;
        this.successfullySubmitted = false;
        this.disableRemoveButton = false;
        break;
      default:
        break;
    }

    this.displayableFileObj.status = status;
  }

  private validateFileType(fileName: string): void {
    const fileExtension = fileName.split('.').pop();

    const isInAllowedExtensions = (array: string[], extension: string) => {
      return array.indexOf('.' + extension.toLowerCase()) > -1;
    };

    /** Check for accepted file extension */
    if (!isInAllowedExtensions(this.allowedExtensions, fileExtension)) {
      this.setStatus(FILE_VALIDATION_FAILURE_STATUS.FILE_TYPE);
    }
  }

  preUploadSingleFileBehavior() { /** placeholder, Extendable */};
  async uploadSingleFile(): Promise<void> {
    
    this.preUploadSingleFileBehavior();

    // Styling
    this.setStatus(FILE_UPLOAD_STATUS.PENDING);

    this.requiredDocsUploadService.trackNewPendingUpload();
    const response = await this.uploadSingleFileService(
      this.actualFileObj,
      this.displayableFileObj
    );

    this.requiredDocsUploadService.uploadAttemptComplete();

    /** Check if this is an error response, if not, save it */
    if (!(response instanceof HttpErrorResponse)) {
      // Styling
      this.setStatus(FILE_UPLOAD_STATUS.SUCCESS);
      this.displayableFileObj.documentRecord = response.documentRecord;
      this.onUploadFile(this);
    } else {
      // Styling
      this.setStatus(FILE_UPLOAD_STATUS.FAILURE);
    }

  }

  removeSingleFile(): void {
    this.onRemoveFile(this); 
  }

  async uploadSingleFileService(fileToUpload: File, displayableFileObj: DisplayableFile): Promise<HttpErrorResponse | SuccessResponse> {
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const serviceUrl = '/sxesvc/api/v1/dcnupv/document/{ubk}/{ddValue}';
    const updatedUrl = this.getUpdatedServiceUrl(serviceUrl);

    const payload = new FormData();

    payload.append('file', fileToUpload);

    this.customPayloadAdditions(payload, displayableFileObj);

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: updatedUrl,
      POST_OR_PUT_PAYLOAD: payload,
      contentType: 'force_undefined'
    });

    if(loginInfo?.userId){
      requestConfig.headers = new HttpHeaders({});
      requestConfig.headers = requestConfig.headers.set('fromDP', loginInfo.userId);
    }

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest<HttpErrorResponse | SuccessResponse>(requestConfig).catch((fail) => fail);
  }

  getUpdatedServiceUrl(url: string) {
    let curBrand = this.brandService.getCurrentBrand();
    let ddValue = curBrand.ddToken;
    let ubk = curBrand.ubk;

    if (ddValue && ubk) {
      const updatedUrl = url.trim()
        .replace('{ddValue}', ddValue)
        .replace('{ubk}', ubk);
      return updatedUrl;
    }
  }

  compareWithFn(option: string , value: string ) {
    if (!value) { return false; }
    return option === value;
  }

}
